import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Shield, ArrowLeft, AlertTriangle, TrendingDown, Lightbulb, Lock, Download, Loader2, Crown, Sparkles, Zap, Volume2, VolumeX, Pause, Play } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import QuickNav from '@/components/QuickNav';

import { API } from '@/lib/api';

const AnalysisResult = () => {
  const { analysisId } = useParams();
  const navigate = useNavigate();
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quota, setQuota] = useState(null);
  const [audioLoading, setAudioLoading] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioElement, setAudioElement] = useState(null);

  useEffect(() => {
    fetchAnalysis();
    fetchQuota();
  }, [analysisId]);

  const fetchQuota = async () => {
    try {
      const response = await axios.get(`${API}/analysis/quota`, { withCredentials: true });
      setQuota(response.data);
    } catch (error) {
      console.error('Error fetching quota:', error);
    }
  };

  const fetchAnalysis = async () => {
    try {
      const response = await axios.get(`${API}/analysis/${analysisId}`, {
        withCredentials: true
      });
      setAnalysis(response.data);
    } catch (error) {
      console.error('Error fetching analysis:', error);
      toast.error('Erro ao carregar análise');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const handlePlayAudio = async () => {
    // Prevent multiple clicks while loading
    if (audioLoading) {
      return;
    }
    
    // If audio is already playing, pause it
    if (audioElement && isPlaying) {
      audioElement.pause();
      setIsPlaying(false);
      return;
    }
    
    // If audio exists but paused, resume
    if (audioElement && !isPlaying) {
      try {
        await audioElement.play();
        setIsPlaying(true);
      } catch (e) {
        console.error('Error resuming audio:', e);
        // If can't resume, reset and try generating again
        setAudioElement(null);
      }
      return;
    }
    
    // Generate new audio
    setAudioLoading(true);
    try {
      const sessionToken = localStorage.getItem('session_token');
      const response = await axios.post(
        `${API}/analysis/${analysisId}/tts`,
        {},
        { 
          withCredentials: true,
          responseType: 'blob',
          headers: sessionToken ? { Authorization: `Bearer ${sessionToken}` } : {},
          timeout: 120000 // 2 minutes timeout for long audio generation
        }
      );
      
      const audioBlob = new Blob([response.data], { type: 'audio/mpeg' });
      const audioUrl = URL.createObjectURL(audioBlob);
      const audio = new Audio(audioUrl);
      
      audio.onended = () => {
        setIsPlaying(false);
      };
      
      audio.onerror = (e) => {
        console.error('Audio playback error:', e);
        toast.error('Erro ao reproduzir áudio. Tente novamente.');
        setIsPlaying(false);
        setAudioElement(null);
      };

      audio.oncanplaythrough = () => {
        // Audio is ready to play
        audio.play().then(() => {
          setIsPlaying(true);
          toast.success('Reproduzindo análise em áudio');
        }).catch((e) => {
          console.error('Error playing audio:', e);
          toast.error('Erro ao iniciar reprodução');
        });
      };
      
      setAudioElement(audio);
    } catch (error) {
      console.error('Error generating TTS:', error);
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        toast.error('Tempo esgotado. O áudio está sendo gerado, aguarde e tente novamente.');
      } else if (error.response?.status === 404) {
        toast.error('Análise não encontrada');
      } else {
        toast.error('Erro ao gerar áudio. Tente novamente em alguns segundos.');
      }
    } finally {
      setAudioLoading(false);
    }
  };

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (audioElement) {
        audioElement.pause();
        audioElement.src = '';
      }
    };
  }, [audioElement]);

  const getRiskConfig = (level) => {
    switch (level) {
      case 'alto':
        return {
          icon: '🔴',
          label: 'Alto Risco',
          color: 'text-red-500',
          bgColor: 'bg-red-500/10',
          borderColor: 'border-red-500/30',
          glowColor: 'shadow-[0_0_30px_rgba(239,68,68,0.3)]'
        };
      case 'medio':
        return {
          icon: '🟡',
          label: 'Risco Medio',
          color: 'text-yellow-400',
          bgColor: 'bg-yellow-400/10',
          borderColor: 'border-yellow-400/30',
          glowColor: 'shadow-[0_0_30px_rgba(250,204,21,0.3)]'
        };
      case 'baixo':
        return {
          icon: '🟢',
          label: 'Baixo Risco',
          color: 'text-[#00FF94]',
          bgColor: 'bg-[#00FF94]/10',
          borderColor: 'border-[#00FF94]/30',
          glowColor: 'shadow-[0_0_30px_rgba(0,255,148,0.3)]'
        };
      default:
        return {
          icon: '🟡',
          label: 'Risco Medio',
          color: 'text-yellow-400',
          bgColor: 'bg-yellow-400/10',
          borderColor: 'border-yellow-400/30',
          glowColor: 'shadow-[0_0_30px_rgba(250,204,21,0.3)]'
        };
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-[#00FF94] animate-spin mx-auto mb-4" />
          <p className="text-gray-400">Carregando sua análise...</p>
        </div>
      </div>
    );
  }

  if (!analysis) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-400 mb-4">Análise não encontrada</p>
          <Button onClick={() => navigate('/')} variant="ghost" className="text-[#00FF94]">
            Voltar ao início
          </Button>
        </div>
      </div>
    );
  }

  const riskConfig = getRiskConfig(analysis.risk_level);

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Header */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass-heavy">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Button 
              variant="ghost" 
              className="text-gray-400 hover:text-white ml-12 sm:ml-14"
              onClick={() => navigate('/')}
              data-testid="back-home-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Nova Análise
            </Button>
            <div className="flex items-center gap-2">
              <img src="/logo-evite-dividas.png" alt="Evite Dívidas" className="w-12 h-12 object-contain" />
              <span className="font-bold text-white">Evite Dívidas</span>
            </div>
          </div>
        </div>
      </nav>

      {/* Result Content */}
      <main className="pt-24 pb-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Risk Level Banner */}
          <motion.div
            className={`rounded-3xl p-8 mb-6 border ${riskConfig.bgColor} ${riskConfig.borderColor} ${riskConfig.glowColor}`}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            <div className="text-center">
              <div className="text-6xl mb-4">{riskConfig.icon}</div>
              <h1 className={`text-4xl sm:text-5xl font-black ${riskConfig.color} mb-2`} data-testid="risk-level">
                {riskConfig.label}
              </h1>
              <p className="text-gray-400 text-lg">
                Classificação da sua decisão financeira
              </p>
            </div>
          </motion.div>

          {/* Audio Player - Prominent at the top */}
          <motion.div
            className="bg-[#0A0A0A] border border-white/10 rounded-2xl p-4 mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
          >
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#00FF94]/20 flex items-center justify-center">
                  <Volume2 className="w-5 h-5 text-[#00FF94]" />
                </div>
                <div>
                  <p className="text-sm font-medium text-white">Ouvir Análise</p>
                  <p className="text-xs text-gray-500">Escute sua análise em áudio</p>
                </div>
              </div>
              <Button
                className={`rounded-full font-medium px-6 ${
                  isPlaying 
                    ? 'bg-amber-500 hover:bg-amber-600 text-black' 
                    : 'bg-[#00FF94] hover:bg-[#00CC76] text-black'
                }`}
                onClick={handlePlayAudio}
                disabled={audioLoading}
                data-testid="play-audio-btn-top"
              >
                {audioLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Gerando...
                  </>
                ) : isPlaying ? (
                  <>
                    <Pause className="w-4 h-4 mr-2" />
                    Pausar
                  </>
                ) : audioElement ? (
                  <>
                    <Play className="w-4 h-4 mr-2" />
                    Continuar
                  </>
                ) : (
                  <>
                    <Play className="w-4 h-4 mr-2" />
                    Reproduzir
                  </>
                )}
              </Button>
            </div>
          </motion.div>

          {/* Premium Analysis Badge */}
          {analysis.is_premium_analysis && (
            <motion.div
              className="bg-gradient-to-r from-amber-500/20 to-yellow-500/20 border border-amber-400/30 rounded-2xl p-4 mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05 }}
              data-testid="premium-analysis-badge"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center">
                  <Crown className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <h3 className="font-bold text-amber-400 text-sm">Análise Premium Personalizada</h3>
                  <p className="text-xs text-gray-400">
                    Esta análise considerou seus dados do Organizador de Despesas para recomendações mais precisas
                  </p>
                </div>
              </div>
            </motion.div>
          )}

          {/* Decision Summary */}
          <motion.div
            className="bg-[#0A0A0A] border border-white/5 rounded-2xl p-6 mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <h2 className="text-lg font-semibold text-white mb-3">Sua Decisão</h2>
            <p className="text-gray-400" data-testid="decision-summary">{analysis.decision_description}</p>
            {analysis.total_value > 0 && analysis.monthly_income > 0 && (
              <div className="grid grid-cols-2 gap-4 mt-4 pt-4 border-t border-white/5">
                <div>
                  <p className="text-sm text-gray-500">Valor Total</p>
                  <p className="text-xl font-bold text-white font-mono">
                    R$ {analysis.total_value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Renda Mensal</p>
                  <p className="text-xl font-bold text-white font-mono">
                    R$ {analysis.monthly_income.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </p>
                </div>
              </div>
            )}
          </motion.div>

          {/* Alerts */}
          <motion.div
            className="bg-[#0A0A0A] border border-white/5 rounded-2xl p-6 mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
          >
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-yellow-400" />
              Alertas Importantes
            </h2>
            <ul className="space-y-3">
              {analysis.alerts.map((alert, index) => (
                <li key={index} className="flex items-start gap-3">
                  <span className="w-2 h-2 rounded-full bg-yellow-400 mt-2 flex-shrink-0" />
                  <span className="text-gray-300">{alert}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Budget Impact */}
          <motion.div
            className="bg-[#0A0A0A] border border-white/5 rounded-2xl p-6 mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <TrendingDown className="w-5 h-5 text-red-400" />
              Impacto no Orçamento
            </h2>
            <p className="text-gray-300" data-testid="budget-impact">{analysis.budget_impact}</p>
          </motion.div>

          {/* Conservative Alternative */}
          <motion.div
            className="bg-[#00FF94]/5 border border-[#00FF94]/20 rounded-2xl p-6 mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
          >
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Lightbulb className="w-5 h-5 text-[#00FF94]" />
              Alternativa Mais Segura
            </h2>
            <p className="text-gray-300" data-testid="alternative">{analysis.conservative_alternative}</p>
          </motion.div>

          {/* Upgrade CTA - Show for free users */}
          {quota && quota.subscription_type === 'free' && (
            <motion.div
              className="bg-gradient-to-r from-[#00FF94]/10 via-[#0A0A0A] to-[#00FF94]/10 border border-[#00FF94]/30 rounded-2xl p-6 mb-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#00FF94]/20 flex items-center justify-center">
                    <Crown className="w-6 h-6 text-[#00FF94]" />
                  </div>
                  <div>
                    <h3 className="font-bold text-white mb-1">Gostou da análise?</h3>
                    <p className="text-sm text-gray-400">
                      Assine e tenha acesso a mais análises + organizador de despesas
                    </p>
                  </div>
                </div>
                <Button 
                  className="bg-[#00FF94] text-black hover:bg-[#00CC76] rounded-full font-bold px-6 whitespace-nowrap"
                  onClick={() => navigate('/pricing')}
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  Ver Planos
                </Button>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4 pt-4 border-t border-white/10">
                <div className="flex items-center gap-2 text-sm text-gray-300">
                  <Zap className="w-4 h-4 text-[#00FF94]" />
                  5 análises/mês
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-300">
                  <Zap className="w-4 h-4 text-[#00FF94]" />
                  Organizador de despesas
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-300">
                  <Zap className="w-4 h-4 text-[#00FF94]" />
                  Metas de economia
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-300">
                  <Zap className="w-4 h-4 text-[#00FF94]" />
                  A partir de R$ 9,90
                </div>
              </div>
            </motion.div>
          )}

          {/* CTA Organizador de Despesas */}
          <motion.div
            className="bg-gradient-to-r from-[#00FF94]/10 to-[#0A0A0A] border border-[#00FF94]/30 rounded-2xl p-6 mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.55 }}
          >
            <h3 className="text-xl font-bold text-white mb-2">
              Quer ir além da análise?
            </h3>
            <p className="text-gray-400 mb-4">
              Use nosso organizador de despesas e acompanhe sua vida financeira mês a mês.
            </p>
            <Button
              className="bg-[#00FF94] text-black hover:bg-[#00CC76] rounded-full font-semibold px-6"
              onClick={() => navigate('/expenses')}
              data-testid="expenses-cta-btn"
            >
              👉 Organizar minhas despesas
            </Button>
          </motion.div>

          {/* Legal Disclaimer */}
          <motion.div
            className="bg-[#0A0A0A] border border-white/5 rounded-xl p-4 mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
          >
            <div className="flex items-start gap-3">
              <Lock className="w-5 h-5 text-gray-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-gray-500">
                Esta análise não é uma recomendação financeira, mas um alerta preventivo para ajudar você a decidir com mais consciência. 
                O Evite Dívidas não oferece consultoria financeira, não indica investimentos e não garante resultados.
              </p>
            </div>
          </motion.div>

          {/* Actions */}
          <motion.div
            className="flex flex-col sm:flex-row gap-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
          >
            <Button 
              size="lg"
              className="flex-1 bg-[#00FF94] text-black hover:bg-[#00CC76] rounded-full font-bold py-6"
              onClick={() => navigate('/analyze')}
              data-testid="new-analysis-btn"
            >
              Nova Análise
            </Button>
            <Button 
              size="lg"
              variant="outline"
              className="flex-1 border-white/10 text-white hover:bg-white/5 rounded-full font-medium py-6"
              onClick={() => {
                window.open(`${API}/analysis/${analysisId}/pdf`, '_blank');
              }}
              data-testid="download-pdf-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Baixar PDF
            </Button>
          </motion.div>
        </div>
      </main>

      {/* Quick Navigation */}
      <QuickNav />
    </div>
  );
};

export default AnalysisResult;
